import { PostsAdsApi } from "./posts-ads-api";
import { PostsAdsRender } from "./posts-ads-render";
import { AdTypes } from "../../constants/ad-types";
import { postsAdsFormBase, type PostsAdsForm } from "./posts-ads-form";

export class PostsAdsHandler {
    private readonly form: PostsAdsForm;

    public constructor(form: PostsAdsForm) {
        this.form = form;
    }

    public async bootstrapInitial(): Promise<void> {
        await this.applyAdsToPendingCards();
    }

    public async applyAdsToPendingCards(): Promise<void> {
        const cards = this.form.getPostCards().filter((card) => this.hasPendingAdSlot(card));
        if (!cards.length) return;

        const placeholderCards = cards.filter((card) => this.needsPlaceholderAd(card));
        const linkerCards = cards.filter((card) => this.needsProductLinkerAd(card));

        const totalNeed = placeholderCards.length + linkerCards.length;
        const allAds = totalNeed > 0 ? await PostsAdsApi.fetchAllAds(Math.max(10, totalNeed * 2)) : [];
        const placeHolderAds = allAds.filter((ad) => String(ad.ad_type || "").toUpperCase() === AdTypes.PLACE_HOLDER);
        const productLinkerAds = allAds.filter(
            (ad) => String(ad.ad_type || "").toUpperCase() === AdTypes.PRODUCT_LINKER,
        );
        const productLinkerOgpAds = allAds.filter(
            (ad) => String(ad.ad_type || "").toUpperCase() === AdTypes.PRODUCT_LINKER_OGP,
        );
        const mixedProductLinkerAds = [...productLinkerAds, ...productLinkerOgpAds];

        placeholderCards.forEach((card, index) => {
            const ad = placeHolderAds[index % Math.max(1, placeHolderAds.length)];
            if (!ad) return;
            if (PostsAdsRender.shouldShowPlaceholderAd(ad)) {
                PostsAdsRender.applyPlaceholderAd(card, ad);
            } else {
                PostsAdsRender.skipPlaceholderAd(card);
            }
            this.syncCardDoneState(card);
        });

        for (let index = 0; index < linkerCards.length; index += 1) {
            const card = linkerCards[index];
            const ad = mixedProductLinkerAds[index % Math.max(1, mixedProductLinkerAds.length)];
            if (!ad) continue;

            const adType = String(ad.ad_type || "").toUpperCase();
            if (adType === AdTypes.PRODUCT_LINKER_OGP) {
                await PostsAdsRender.applyProductLinkerOgpAd(card, ad);
            } else {
                PostsAdsRender.applyProductLinkerAd(card, ad);
            }
            this.syncCardDoneState(card);
        }

        cards.forEach((card) => this.syncCardDoneState(card));
    }

    private hasPendingAdSlot(card: HTMLElement): boolean {
        return this.needsPlaceholderAd(card) || this.needsProductLinkerAd(card);
    }

    private needsPlaceholderAd(card: HTMLElement): boolean {
        if (!this.isPlaceholderTarget(card)) return false;
        const placeholder = postsAdsFormBase.getPlaceholder(card);
        return placeholder instanceof HTMLElement && placeholder.dataset.adApplied !== "1";
    }

    private needsProductLinkerAd(card: HTMLElement): boolean {
        if (!this.isProductLinkerTarget(card)) return false;
        const linker = postsAdsFormBase.getLinker(card);
        return linker instanceof HTMLElement && linker.dataset.adApplied !== "1";
    }

    private syncCardDoneState(card: HTMLElement): void {
        if (this.hasPendingAdSlot(card)) {
            delete card.dataset.postsAdsDone;
            return;
        }

        card.dataset.postsAdsDone = "1";
    }

    public isProductLinkerTarget(card: HTMLElement): boolean {
        const explicit = String(
            card.dataset.roleAdProductLinkerTarget || card.dataset.adProductLinkerTarget || "",
        ).trim();
        if (explicit === "1") return true;
        if (explicit === "0") return false;

        const linker = postsAdsFormBase.getLinker(card);
        if (linker instanceof HTMLElement && linker.dataset.productLinkerUnresolved === "1") {
            return true;
        }

        return !this.hasLinkedProductId(card);
    }

    public isPlaceholderTarget(card: HTMLElement): boolean {
        const explicit = String(card.dataset.roleAdPlaceholderTarget || card.dataset.adPlaceholderTarget || "").trim();
        if (explicit === "1") return true;
        if (explicit === "0") return false;

        return !this.hasAnyMedia(card);
    }

    public hasLinkedProductId(card: HTMLElement): boolean {
        const productIdInput = postsAdsFormBase.getProductIdInput(card);
        if (!(productIdInput instanceof HTMLInputElement)) return false;

        const raw = String(productIdInput.value || "").trim();
        if (!raw) return false;
        const num = Number(raw);
        return Number.isFinite(num) && num > 0;
    }

    public hasAnyMedia(card: HTMLElement): boolean {
        const hasImage = postsAdsFormBase.getBoardImage(card) instanceof HTMLElement;
        const hasVideo = postsAdsFormBase.getBoardVideo(card) instanceof HTMLElement;
        const hasSnsTiles = postsAdsFormBase.getSnsTile(card) instanceof HTMLElement;
        if (hasImage || hasVideo || hasSnsTiles) return true;

        const snsData = postsAdsFormBase.getSnsData(card);
        if (!(snsData instanceof HTMLElement)) return false;

        const raw = String(snsData.dataset.snsLinks || "").trim();
        if (!raw) return false;

        try {
            const parsed = JSON.parse(raw) as unknown;
            return Array.isArray(parsed) && parsed.length > 0;
        } catch {
            return false;
        }
    }
}
